// Dashboard API Service Layer
const API_BASE_URL = 'https://api.stratiqx.ai'

// Type definitions based on API documentation
export interface HealthOverview {
  healthScore: number
  systemStatus: 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR'
  totalExecutions: number
  successRate: number
  averageProcessingTime: number
  activeExecutions: number
  completedToday: number
  failedToday: number
  errorSources: {
    d1Database: number
    aiService: number
    r2Storage: number
    pdfGenerator: number
    other: number
  }
  criticalIssues: number
  serviceDowntime: number
  alerts?: Alert[]
}

export interface FailureCategory {
  category: 'DATABASE_CONSTRAINT' | 'SCHEMA_MISSING' | 'D1_CONNECTION' | 
           'R2_CONNECTION' | 'AI_SERVICE' | 'TIMEOUT' | 'RATE_LIMIT' | 
           'PDF_GENERATION' | 'OTHER'
  count: number
  percentage: number
  latestOccurrence: string
  sampleErrorMessage: string
  troubleshootingRecommendation: string
  priority: 'HIGH' | 'MEDIUM' | 'LOW'
  affectedServices: string[]
  trend?: 'up' | 'down' | 'stable'
}

export interface ExecutionItem {
  trackingId: string
  companyName: string
  industry?: string
  status: 'processing' | 'queued' | 'completed' | 'failed'
  currentStep: string
  progressPercentage?: number
  startTime: string
  duration: number
  isLongRunning?: boolean
  errorMessage?: string
}

export interface Alert {
  id: string
  priority: 'HIGH' | 'MEDIUM' | 'LOW'
  title: string
  description: string
  service: string
  errorCode?: string
  affectedCount: number
  recommendedAction: string
  timestamp: string
  autoResolvable: boolean
}

export interface ServiceHealth {
  name: string
  status: 'healthy' | 'degraded' | 'down' | 'maintenance'
  uptime: number
  responseTime: number
  errorRate: number
  errorCount24h: number
  lastError?: string
  throughput?: number
  capacity?: number
}

export interface DashboardData {
  health: HealthOverview | null
  failures: FailureCategory[]
  executions: ExecutionItem[]
  services: ServiceHealth[]
  alerts: Alert[]
  lastUpdated: Date
}

export class DashboardService {
  private static async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}/v1/${endpoint}`
    
    // Get the stored access token
    const accessToken = localStorage.getItem('stratiqx_access_token')
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      // Add Authorization header if token exists
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...options.headers,
    }

    const config: RequestInit = {
      ...options,
      headers: defaultHeaders,
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error(`Dashboard API Error (${response.status}) for ${url}:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        })
        throw new Error(`API request failed: ${response.status} ${response.statusText}`)
      }

      return response.json()
    } catch (error) {
      console.error('Dashboard API request error:', error)
      throw error
    }
  }

  /**
   * Fetch system health overview
   */
  static async getHealthOverview(): Promise<HealthOverview> {
    try {
      console.log('Fetching dashboard health overview...')
      const response = await this.makeRequest<any>('dashboard/overview')
      
      // Transform API response to match our interface
      return this.transformHealthData(response)
    } catch (error) {
      console.error('Failed to fetch health overview:', error)
      throw error
    }
  }

  /**
   * Fetch failure patterns with optional timeframe
   */
  static async getFailures(timeframe: string = '24h'): Promise<FailureCategory[]> {
    try {
      console.log(`Fetching failure patterns for timeframe: ${timeframe}`)
      const response = await this.makeRequest<any>(`dashboard/failures?timeframe=${timeframe}`)
      
      // Transform API response to match our interface
      return this.transformFailuresData(response)
    } catch (error) {
      console.error('Failed to fetch failures:', error)
      return []
    }
  }

  /**
   * Fetch execution monitoring data
   */
  static async getExecutions(status: string = 'all', limit: number = 25): Promise<ExecutionItem[]> {
    try {
      console.log(`Fetching executions with status: ${status}, limit: ${limit}`)
      const response = await this.makeRequest<any>(`dashboard/executions?status=${status}&limit=${limit}`)
      
      // Transform API response to match our interface
      return this.transformExecutionsData(response)
    } catch (error) {
      console.error('Failed to fetch executions:', error)
      return []
    }
  }

  /**
   * Fetch all dashboard data in parallel
   */
  static async getAllDashboardData(): Promise<DashboardData> {
    try {
      console.log('Fetching all dashboard data...')
      
      // Fetch all data in parallel for better performance
      const [health, failures, executions] = await Promise.all([
        this.getHealthOverview().catch(err => {
          console.error('Health overview failed:', err)
          return null
        }),
        this.getFailures('24h').catch(err => {
          console.error('Failures fetch failed:', err)
          return []
        }),
        this.getExecutions('all', 25).catch(err => {
          console.error('Executions fetch failed:', err)
          return []
        })
      ])

      // Extract services and alerts from health data
      const services = health ? this.extractServiceHealth(health) : []
      const alerts = health?.alerts || []

      return {
        health,
        failures,
        executions,
        services,
        alerts,
        lastUpdated: new Date()
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      throw error
    }
  }

  /**
   * Transform API response to match HealthOverview interface
   */
  private static transformHealthData(apiResponse: any): HealthOverview {
    // Handle the actual API response structure
    const dashboard = apiResponse.dashboard || apiResponse
    const health = dashboard.health || dashboard
    const metrics = dashboard.metrics || {}
    const failures = dashboard.failures || {}
    
    // Calculate error sources from failures data
    const errorSources = {
      d1Database: 0,
      aiService: 0,
      r2Storage: 0,
      pdfGenerator: 0,
      other: 0
    }
    
    // Parse failure sources if available
    if (failures.by_source && Array.isArray(failures.by_source)) {
      failures.by_source.forEach((source: any) => {
        if (source.source?.includes('D1') || source.source?.includes('database')) {
          errorSources.d1Database += source.count || 0
        } else if (source.source?.includes('AI') || source.source?.includes('orchestrator')) {
          errorSources.aiService += source.count || 0
        } else if (source.source?.includes('R2') || source.source?.includes('storage')) {
          errorSources.r2Storage += source.count || 0
        } else if (source.source?.includes('PDF') || source.source?.includes('pdf')) {
          errorSources.pdfGenerator += source.count || 0
        } else {
          errorSources.other += source.count || 0
        }
      })
    }
    
    return {
      healthScore: health.score || 0,
      systemStatus: health.status || 'UNKNOWN',
      totalExecutions: health.total_executions || metrics.total || 0,
      successRate: health.success_rate || 0,
      averageProcessingTime: Math.round((health.avg_processing_time_ms || 0) / 1000), // Convert ms to seconds
      activeExecutions: metrics.running || 0,
      completedToday: metrics.completed || 0,
      failedToday: metrics.failed || 0,
      errorSources,
      criticalIssues: failures.recent?.filter((f: any) => f.priority === 'HIGH').length || 0,
      serviceDowntime: 0, // Not available in current API
      alerts: [] // Will be populated from failures
    }
  }

  /**
   * Transform API response to match FailureCategory interface
   */
  private static transformFailuresData(apiResponse: any): FailureCategory[] {
    const failureAnalysis = apiResponse.failure_analysis || apiResponse
    const detailedFailures = failureAnalysis.detailed_failures || []
    const errorPatterns = failureAnalysis.error_patterns || []
    
    // Combine detailed failures and error patterns
    const allFailures = [...detailedFailures, ...errorPatterns]
    
    if (allFailures.length === 0) {
      return [] // No failures in the timeframe
    }

    return allFailures.map((failure: any) => ({
      category: failure.error_type || failure.pattern || 'OTHER',
      count: failure.count || failure.occurrences || 0,
      percentage: failure.percentage || 0,
      latestOccurrence: failure.latest_occurrence || failure.last_seen || 'Unknown',
      sampleErrorMessage: failure.sample_error || failure.error_message || '',
      troubleshootingRecommendation: failure.recommendation || failureAnalysis.troubleshooting_recommendations?.[0] || 'Review error logs for details',
      priority: failure.severity || 'LOW',
      affectedServices: failure.affected_services || [],
      trend: failure.trend || 'stable'
    }))
  }

  /**
   * Transform API response to match ExecutionItem interface
   */
  private static transformExecutionsData(apiResponse: any): ExecutionItem[] {
    const executionsData = apiResponse.executions || apiResponse
    const recent = executionsData.recent || []
    
    if (!Array.isArray(recent)) {
      console.warn('Executions recent is not an array:', recent)
      return []
    }

    return recent.map((execution: any) => {
      // Calculate duration from submitted and processed times
      let duration = 0
      if (execution.submitted_at && execution.processed_at) {
        const start = new Date(execution.submitted_at).getTime()
        const end = new Date(execution.processed_at).getTime()
        duration = Math.round((end - start) / 1000) // Convert to seconds
      }
      
      return {
        trackingId: execution.tracking_id || '',
        companyName: execution.company_name || 'Unknown',
        industry: execution.industry || '',
        status: execution.processing_status || 'queued',
        currentStep: execution.processing_status === 'completed' ? 'Complete' : 
                    execution.processing_status === 'failed' ? 'Failed' : 'Processing',
        progressPercentage: execution.processing_status === 'completed' ? 100 : 0,
        startTime: execution.submitted_at || new Date().toISOString(),
        duration,
        isLongRunning: duration > 600,
        errorMessage: execution.error_message
      }
    })
  }

  /**
   * Extract service health from health overview data
   */
  private static extractServiceHealth(health: HealthOverview): ServiceHealth[] {
    // Create service health based on error sources
    const services: ServiceHealth[] = [
      {
        name: 'AI Orchestrator',
        status: health.errorSources.aiService > 10 ? 'degraded' : 
                health.errorSources.aiService > 0 ? 'degraded' : 'healthy',
        uptime: 100 - (health.errorSources.aiService / Math.max(health.totalExecutions, 1) * 100),
        responseTime: health.averageProcessingTime * 1000,
        errorRate: (health.errorSources.aiService / Math.max(health.totalExecutions, 1) * 100),
        errorCount24h: health.errorSources.aiService,
        throughput: Math.min(100, (health.activeExecutions / 10) * 100),
        capacity: Math.min(100, (health.activeExecutions / 15) * 100)
      },
      {
        name: 'D1 Database',
        status: health.errorSources.d1Database > 5 ? 'degraded' : 
                health.errorSources.d1Database > 0 ? 'degraded' : 'healthy',
        uptime: 100 - (health.errorSources.d1Database / Math.max(health.totalExecutions, 1) * 100),
        responseTime: 50,
        errorRate: (health.errorSources.d1Database / Math.max(health.totalExecutions, 1) * 100),
        errorCount24h: health.errorSources.d1Database,
        throughput: 75,
        capacity: 45
      },
      {
        name: 'R2 Storage',
        status: health.errorSources.r2Storage > 3 ? 'degraded' : 
                health.errorSources.r2Storage > 0 ? 'degraded' : 'healthy',
        uptime: 100 - (health.errorSources.r2Storage / Math.max(health.totalExecutions, 1) * 100),
        responseTime: 120,
        errorRate: (health.errorSources.r2Storage / Math.max(health.totalExecutions, 1) * 100),
        errorCount24h: health.errorSources.r2Storage,
        throughput: 60,
        capacity: 35
      },
      {
        name: 'PDF Generator',
        status: health.errorSources.pdfGenerator > 5 ? 'degraded' : 
                health.errorSources.pdfGenerator > 0 ? 'degraded' : 'healthy',
        uptime: 100 - (health.errorSources.pdfGenerator / Math.max(health.totalExecutions, 1) * 100),
        responseTime: 2500,
        errorRate: (health.errorSources.pdfGenerator / Math.max(health.totalExecutions, 1) * 100),
        errorCount24h: health.errorSources.pdfGenerator,
        throughput: 40,
        capacity: 60
      }
    ]

    return services
  }
}